import React, { useEffect, useState } from 'react';

import { useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { useFlags } from '@atlaskit/flag';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import type { AuthProvider, MediaClientConfig } from '@atlaskit/media-core';
import { MediaImage } from '@atlaskit/media-image';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Pressable, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { useProductDependencies } from '../../../../../controllers/use-product-dependencies';
import { PlaceholderThumbnail } from '../placeholder';

import { messages } from './messages';

const baseStyles = xcss({
	height: '40px',
	width: '44px',
	borderRadius: 'border.radius.100',
	border: 'none',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
	position: 'relative' as any,
	backgroundSize: 'cover',
	backgroundPosition: 'center',
	backgroundRepeat: 'no-repeat',
});

const selectedStyles = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
	boxShadow: `inset 0 0 0 2px ${token('color.border.selected')}` as any,
});

type UploadedImageThumbnailProps = {
	contentId: string;
	imageId: string;
	isSelected: boolean;
	onClick: () => void;
	children?: React.ReactNode;
	mediaAuthProvider?: AuthProvider;
	collectionName?: string;
};

const getImageMediaLocation = (
	activeContentId: string,
	maybeCustomCoverImage: string,
): [string, string] => {
	if (maybeCustomCoverImage.indexOf('/') !== -1) {
		const temp = maybeCustomCoverImage.split('/');
		return [temp[0], temp[1]];
	}
	return [activeContentId, maybeCustomCoverImage];
};

export const UploadedImageThumbnail = ({
	contentId,
	imageId: maybeCustomCoverImage,
	isSelected,
	onClick,
	children,
	mediaAuthProvider,
	collectionName,
}: UploadedImageThumbnailProps) => {
	const { createRendererMediaProvider } = useProductDependencies();
	const { showFlag } = useFlags();
	const { formatMessage } = useIntl();

	const [mediaConfig, setMediaConfig] = useState<{
		collection: string | undefined;
		authProvider: MediaClientConfig['authProvider'];
	} | null>(null);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [collectionId, imageId] = getImageMediaLocation(contentId, maybeCustomCoverImage);

	// Setup media config
	useEffect(() => {
		const createMediaConfig = async () => {
			if (createRendererMediaProvider) {
				const { viewMediaClientConfig } = await createRendererMediaProvider({
					contentId: collectionId,
					isExternalShare: false,
				});

				if (viewMediaClientConfig === undefined) {
					return null;
				}
				setMediaConfig({
					collection: `contentId-${collectionId}`,
					authProvider: viewMediaClientConfig.authProvider,
				});
			}
		};

		void createMediaConfig();
	}, [collectionId, createRendererMediaProvider]);

	const resolvedAuthProvider = mediaAuthProvider ?? mediaConfig?.authProvider;
	const resolvedCollectionName = collectionName ?? mediaConfig?.collection;

	if (!resolvedAuthProvider) {
		return <PlaceholderThumbnail />;
	}

	return (
		<MediaImage
			identifier={{
				mediaItemType: 'file',
				id: imageId,
				collectionName: resolvedCollectionName,
			}}
			//force rerender image on id change until https://product-fabric.atlassian.net/browse/CXP-2899 is fixed
			key={imageId}
			mediaClientConfig={{
				authProvider: resolvedAuthProvider,
			}}
			apiConfig={{
				allowAnimated: true,
				mode: 'full-fit' as const,
				upscale: false,
			}}
		>
			{({ data, loading, error }) => {
				if (loading || !data) {
					return <PlaceholderThumbnail />;
				}
				if (error) {
					void showFlag({
						id: 'upload-image-error',
						title: formatMessage(messages.uploadImageFailedTitle),
						description: formatMessage(messages.uploadImageFailedDescription),
						icon: <StatusErrorIcon label="" color={token('color.icon.danger')} />,
					});

					createAnalyticsEvent({
						type: 'error',
						data: {
							actionSubject: 'image',
							actionSubjectId: 'coverImageSelected',
							source: 'coverImageSelectedComponent',
						},
					}).fire();
					return <PlaceholderThumbnail />;
				}
				return (
					<Pressable
						xcss={[baseStyles, isSelected && selectedStyles]}
						style={{
							backgroundImage: `url(${data.src})`,
						}}
						onClick={onClick}
						testId="uploaded-image-thumbnail"
					>
						{children}
					</Pressable>
				);
			}}
		</MediaImage>
	);
};
